import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    InventoryIcon,
    PlusIcon,
    SearchIcon,
    DeleteIcon,
    EditIcon,
    SaveIcon,
    CloseIcon,
    UploadIcon,
    AlertCircleIcon,
    CheckCircleIcon,
    RefreshIcon,
    HistoryIcon
} from '../components/Icons';
import {
    Package,
    Home,
    Wrench,
    Handshake,
    Tent,
    MapPin,
    CheckCircle2
} from 'lucide-react';

const CATEGORIES = [
    'Zelte & Jurten & Kothen',
    'Planen & Seile',
    'Küche & Kochgeschirr',
    'Werkzeug & Bauholz',
    'Lagerfeuer & Grill',
    'Spiele & Pädagogik',
    'Technik & Audio',
    'Erste Hilfe & Sicherheit',
    'Sonstiges'
];

const STORAGE_TYPES = [
    { value: 'stamm', label: 'Pfadfinderheim / Lagerraum' },
    { value: 'schuppen', label: 'Materialschuppen / Garage' },
    { value: 'home', label: 'Privat / Zuhause bei Mitglied' },
    { value: 'other', label: 'Sonstiger Lagerort' }
];

const CONDITIONS = [
    { value: 'new', label: 'Neu / Neuwertig', color: 'emerald' },
    { value: 'good', label: 'Gut & Einsatzbereit', color: 'blue' },
    { value: 'fair', label: 'Gebraucht / Abnutzung', color: 'amber' },
    { value: 'needs_repair', label: 'Reparaturbedürftig', color: 'rose' },
    { value: 'broken', label: 'Defekt / Nicht einsatzbereit', color: 'rose' }
];

const MaterialManagement = () => {
    const { user, API_URL } = useContext(AuthContext);
    const token = user?.token;

    const [activeTab, setActiveTab] = useState('items'); // 'items' | 'lendings' | 'repairs' | 'home_storage'
    const [summary, setSummary] = useState({
        totalItems: 0,
        totalQuantity: 0,
        storedAtHome: 0,
        externalMaterial: 0,
        needsRepairCount: 0,
        activeLendingsCount: 0,
        categoriesCount: {}
    });

    const [items, setItems] = useState([]);
    const [lendings, setLendings] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Filters for items
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [storageFilter, setStorageFilter] = useState('all');
    const [ownershipFilter, setOwnershipFilter] = useState('all');
    const [conditionFilter, setConditionFilter] = useState('all');

    // Modals
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemFormData, setItemFormData] = useState({
        name: '',
        category: 'Zelte & Jurten & Kothen',
        quantity: 1,
        unit: 'Stück',
        storageType: 'stamm',
        locationDetails: '',
        storedWithUser: '',
        ownership: 'stamm',
        ownerDetails: '',
        condition: 'good',
        needsRepair: false,
        repairNotes: '',
        repairAssignee: '',
        imageUrl: '',
        notes: ''
    });

    const [isLendingModalOpen, setIsLendingModalOpen] = useState(false);
    const [lendingFormData, setLendingFormData] = useState({
        itemId: '',
        borrowerType: 'member',
        borrowerUser: '',
        borrowerName: '',
        borrowerContact: '',
        lendDate: new Date().toISOString().split('T')[0],
        expectedReturnDate: '',
        purpose: '',
        quantity: 1,
        notes: ''
    });

    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const authConfig = useMemo(() => ({
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    const fetchData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [summaryRes, itemsRes, lendingsRes, usersRes] = await Promise.all([
                axios.get(`${API_URL}/inventory/summary`, authConfig),
                axios.get(`${API_URL}/inventory/items`, authConfig),
                axios.get(`${API_URL}/inventory/lendings`, authConfig),
                axios.get(`${API_URL}/users`, authConfig).catch(() => ({ data: [] }))
            ]);
            setSummary(summaryRes.data);
            setItems(itemsRes.data);
            setLendings(lendingsRes.data);
            setUsersList(Array.isArray(usersRes.data) ? usersRes.data : []);
        } catch (err) {
            console.error('Fehler beim Laden des Inventars:', err);
            setError(err.response?.data?.message || 'Fehler beim Laden des Material-Inventars.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchData();
        }
    }, [token, API_URL]);

    // Open Item Modal
    const handleOpenCreateItemModal = () => {
        setEditingItem(null);
        setItemFormData({
            name: '',
            category: 'Zelte & Jurten & Kothen',
            quantity: 1,
            unit: 'Stück',
            storageType: 'stamm',
            locationDetails: '',
            storedWithUser: '',
            ownership: 'stamm',
            ownerDetails: '',
            condition: 'good',
            needsRepair: false,
            repairNotes: '',
            repairAssignee: '',
            imageUrl: '',
            notes: ''
        });
        setIsItemModalOpen(true);
    };

    const handleOpenEditItemModal = (item) => {
        setEditingItem(item);
        setItemFormData({
            name: item.name || '',
            category: item.category || 'Sonstiges',
            quantity: item.quantity || 1,
            unit: item.unit || 'Stück',
            storageType: item.storageType || 'stamm',
            locationDetails: item.locationDetails || '',
            storedWithUser: item.storedWithUser || '',
            ownership: item.ownership || 'stamm',
            ownerDetails: item.ownerDetails || '',
            condition: item.condition || 'good',
            needsRepair: Boolean(item.needsRepair),
            repairNotes: item.repairNotes || '',
            repairAssignee: item.repairAssignee || '',
            imageUrl: item.imageUrl || '',
            notes: item.notes || ''
        });
        setIsItemModalOpen(true);
    };

    // Open Lending Modal
    const handleOpenLendingModal = (preselectedItemId = '') => {
        setLendingFormData({
            itemId: preselectedItemId || (items.length > 0 ? items[0]._id : ''),
            borrowerType: 'member',
            borrowerUser: '',
            borrowerName: '',
            borrowerContact: '',
            lendDate: new Date().toISOString().split('T')[0],
            expectedReturnDate: '',
            purpose: '',
            quantity: 1,
            notes: ''
        });
        setIsLendingModalOpen(true);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        const uploadForm = new FormData();
        uploadForm.append('file', file);

        try {
            const res = await axios.post(`${API_URL}/files/upload`, uploadForm, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            const url = res.data?.url || res.data?.fileUrl;
            if (url) {
                setItemFormData(prev => ({ ...prev, imageUrl: url }));
            }
        } catch (err) {
            console.error('Bild-Upload fehlgeschlagen:', err);
            alert('Upload fehlgeschlagen: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsUploading(false);
        }
    };

    // Submit Item (Create / Update)
    const handleItemSubmit = async (e) => {
        e.preventDefault();
        if (!itemFormData.name.trim()) {
            alert('Bitte Gegenstandsnamen angeben.');
            return;
        }

        setIsSubmitting(true);
        try {
            if (editingItem) {
                await axios.put(`${API_URL}/inventory/items/${editingItem._id}`, itemFormData, authConfig);
                setSuccessMessage('Gegenstand erfolgreich aktualisiert!');
            } else {
                await axios.post(`${API_URL}/inventory/items`, itemFormData, authConfig);
                setSuccessMessage('Gegenstand erfolgreich ins Inventar aufgenommen!');
            }
            setIsItemModalOpen(false);
            fetchData();
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            console.error('Fehler beim Speichern:', err);
            alert('Fehler beim Speichern: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Submit Lending
    const handleLendingSubmit = async (e) => {
        e.preventDefault();
        if (!lendingFormData.itemId) {
            alert('Bitte einen Gegenstand auswählen.');
            return;
        }

        setIsSubmitting(true);
        try {
            await axios.post(`${API_URL}/inventory/lendings`, lendingFormData, authConfig);
            setSuccessMessage('Ausleihe erfolgreich verbucht!');
            setIsLendingModalOpen(false);
            fetchData();
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            console.error('Fehler beim Erfassen der Ausleihe:', err);
            alert('Fehler beim Verleihen: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Return Lending
    const handleReturnLending = async (lendingId) => {
        const returnNotes = window.prompt('Rückgabe-Notiz / Zustand bei Rückgabe (optional):', 'Vollständig & unbeschädigt zurückgebracht');
        if (returnNotes === null) return; // cancelled

        try {
            await axios.post(`${API_URL}/inventory/lendings/${lendingId}/return`, { notes: returnNotes }, authConfig);
            setSuccessMessage('Rückgabe erfolgreich verbucht!');
            fetchData();
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            console.error('Fehler bei Rückbuchung:', err);
            alert('Fehler beim Verbuchen der Rückgabe: ' + (err.response?.data?.message || err.message));
        }
    };

    // Delete Item
    const handleDeleteItem = async (id) => {
        if (!window.confirm('Möchtest du diesen Gegenstand wirklich aus dem Inventar löschen?')) return;
        try {
            await axios.delete(`${API_URL}/inventory/items/${id}`, authConfig);
            setSuccessMessage('Gegenstand gelöscht.');
            fetchData();
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            console.error('Fehler beim Löschen:', err);
            alert('Fehler beim Löschen: ' + (err.response?.data?.message || err.message));
        }
    };

    // Filter Items
    const filteredItems = useMemo(() => {
        return items.filter(item => {
            if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
            if (storageFilter !== 'all' && item.storageType !== storageFilter) return false;
            if (ownershipFilter !== 'all' && item.ownership !== ownershipFilter) return false;
            if (conditionFilter !== 'all' && item.condition !== conditionFilter) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matchName = item.name?.toLowerCase().includes(term);
                const matchLoc = item.locationDetails?.toLowerCase().includes(term);
                const matchOwner = item.ownerDetails?.toLowerCase().includes(term);
                const matchNotes = item.notes?.toLowerCase().includes(term);
                const matchRepair = item.repairNotes?.toLowerCase().includes(term);
                if (!matchName && !matchLoc && !matchOwner && !matchNotes && !matchRepair) return false;
            }
            return true;
        });
    }, [items, categoryFilter, storageFilter, ownershipFilter, conditionFilter, searchTerm]);

    const repairItems = useMemo(() => {
        return items.filter(i => i.needsRepair || i.condition === 'needs_repair' || i.condition === 'broken');
    }, [items]);

    const homeStorageItems = useMemo(() => {
        return items.filter(i => i.storageType === 'home' || i.storedWithUser);
    }, [items]);

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-secondary/10 text-secondary flex items-center justify-center shadow-sm">
                            <Tent className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-extrabold text-surface-on tracking-tight">
                                Material- & Inventarverwaltung
                            </h1>
                            <p className="text-sm text-surface-onVariant mt-0.5">
                                Zelte, Kochausrüstung, dezentrale Lagerung, Leihgaben & Reparatur-Werkstatt
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={fetchData}
                        className="p-2.5 rounded-2xl border border-outlineVariant/50 bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-onVariant transition-all shadow-sm"
                        title="Aktualisieren"
                    >
                        <RefreshIcon className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handleOpenLendingModal()}
                        className="px-4 py-2.5 rounded-2xl border border-outlineVariant/50 bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-on text-sm font-semibold flex items-center gap-2 transition-all shadow-sm"
                    >
                        <HistoryIcon className="w-4 h-4" />
                        Verleihen
                    </button>
                    <button
                        onClick={handleOpenCreateItemModal}
                        className="px-5 py-2.5 rounded-2xl bg-primary text-primary-on hover:bg-primary/90 text-sm font-bold flex items-center gap-2 transition-all shadow-md active:scale-95"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Gegenstand anlegen
                    </button>
                </div>
            </div>

            {/* Notification messages */}
            {successMessage && (
                <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 flex items-center gap-3 animate-fade-in shadow-sm">
                    <CheckCircleIcon className="w-5 h-5 shrink-0" />
                    <p className="text-sm font-medium">{successMessage}</p>
                </div>
            )}

            {error && (
                <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 flex items-center gap-3 animate-fade-in shadow-sm">
                    <AlertCircleIcon className="w-5 h-5 shrink-0" />
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-5 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-surface-onVariant">Gesamtinventar</span>
                        <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                            <Package className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="mt-2">
                        <h3 className="text-2xl md:text-3xl font-black text-surface-on tracking-tight">
                            {summary.totalItems} <span className="text-xs font-normal text-surface-onVariant">Typen</span>
                        </h3>
                        <p className="text-xs text-surface-onVariant mt-0.5">{summary.totalQuantity} Stück / Einheiten gesamt</p>
                    </div>
                </div>

                <div className="p-5 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-surface-onVariant">Privat gelagert</span>
                        <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                            <Home className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="mt-2">
                        <h3 className="text-2xl md:text-3xl font-black text-amber-600 dark:text-amber-400 tracking-tight">
                            {summary.storedAtHome}
                        </h3>
                        <p className="text-xs text-surface-onVariant mt-0.5">Bei Mitgliedern zuhause dezentral</p>
                    </div>
                </div>

                <div className="p-5 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-surface-onVariant">Reparaturbedarf</span>
                        <div className="w-8 h-8 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center">
                            <Wrench className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="mt-2">
                        <h3 className="text-2xl md:text-3xl font-black text-rose-600 dark:text-rose-400 tracking-tight">
                            {summary.needsRepairCount}
                        </h3>
                        <p className="text-xs text-surface-onVariant mt-0.5">Gegenstände brauchen Wartung/Reparatur</p>
                    </div>
                </div>

                <div className="p-5 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-surface-onVariant">Aktuell verliehen</span>
                        <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                            <Handshake className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="mt-2">
                        <h3 className="text-2xl md:text-3xl font-black text-blue-600 dark:text-blue-400 tracking-tight">
                            {summary.activeLendingsCount}
                        </h3>
                        <p className="text-xs text-surface-onVariant mt-0.5">Laufende Leihgaben an Mitglieder / Externe</p>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex items-center gap-2 border-b border-outlineVariant/20 pb-2 overflow-x-auto">
                <button
                    onClick={() => setActiveTab('items')}
                    className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'items' ? 'bg-primary text-primary-on shadow-sm' : 'text-surface-onVariant hover:text-surface-on hover:bg-surface-container'}`}
                >
                    Alle Gegenstände ({items.length})
                </button>
                <button
                    onClick={() => setActiveTab('lendings')}
                    className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'lendings' ? 'bg-primary text-primary-on shadow-sm' : 'text-surface-onVariant hover:text-surface-on hover:bg-surface-container'}`}
                >
                    Ausleihen & Verleih ({lendings.filter(l => l.status === 'active').length})
                </button>
                <button
                    onClick={() => setActiveTab('repairs')}
                    className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'repairs' ? 'bg-primary text-primary-on shadow-sm' : 'text-surface-onVariant hover:text-surface-on hover:bg-surface-container'}`}
                >
                    <span>Werkstatt & Reparatur</span>
                    {repairItems.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-rose-500 text-white font-bold">
                            {repairItems.length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('home_storage')}
                    className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'home_storage' ? 'bg-primary text-primary-on shadow-sm' : 'text-surface-onVariant hover:text-surface-on hover:bg-surface-container'}`}
                >
                    <span>Zuhause gelagert</span>
                    {homeStorageItems.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-700 dark:text-amber-300 font-bold">
                            {homeStorageItems.length}
                        </span>
                    )}
                </button>
            </div>

            {/* TAB 1: ALL INVENTORY ITEMS */}
            {activeTab === 'items' && (
                <div className="space-y-6">
                    {/* Filters */}
                    <div className="p-5 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                            <div className="relative lg:col-span-2">
                                <SearchIcon className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-onVariant pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Suche nach Name, Lagerort, Eigentümer..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary"
                                />
                            </div>

                            <select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                className="px-3.5 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary font-medium"
                            >
                                <option value="all">Alle Kategorien</option>
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>

                            <select
                                value={storageFilter}
                                onChange={(e) => setStorageFilter(e.target.value)}
                                className="px-3.5 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary font-medium"
                            >
                                <option value="all">Alle Lagerorte</option>
                                {STORAGE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>

                            <select
                                value={conditionFilter}
                                onChange={(e) => setConditionFilter(e.target.value)}
                                className="px-3.5 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary font-medium"
                            >
                                <option value="all">Alle Zustände</option>
                                {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Items Grid */}
                    {isLoading ? (
                        <div className="p-12 text-center text-surface-onVariant">
                            <RefreshIcon className="w-8 h-8 animate-spin mx-auto text-primary mb-2" />
                            <p className="text-sm font-medium">Material-Inventar wird geladen...</p>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="p-12 text-center text-surface-onVariant bg-surface-container rounded-3xl border border-outlineVariant/30">
                            <p className="text-base font-semibold text-surface-on">Keine Gegenstände gefunden</p>
                            <p className="text-sm mt-1">Passe die Filter an oder erfasse neue Materialien.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredItems.map(item => {
                                const isExternal = item.ownership === 'external';
                                const isHomeStorage = item.storageType === 'home' || item.storedWithUser;
                                const needsWork = item.needsRepair || item.condition === 'needs_repair' || item.condition === 'broken';

                                return (
                                    <div
                                        key={item._id}
                                        className="p-5 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm flex flex-col justify-between hover:border-outlineVariant/60 transition-all"
                                    >
                                        <div className="space-y-3">
                                            {/* Image or Icon Preview */}
                                            {item.imageUrl ? (
                                                <div className="h-40 rounded-2xl overflow-hidden bg-surface-containerLowest border border-outlineVariant/20 mb-3">
                                                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                                                </div>
                                            ) : null}

                                            {/* Badges */}
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-surface-containerHighest text-surface-on">
                                                    {item.category}
                                                </span>
                                                {isExternal && (
                                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-700 dark:text-amber-300">
                                                        Fremdmaterial
                                                    </span>
                                                )}
                                                {needsWork && (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-rose-500/15 text-rose-700 dark:text-rose-300">
                                                        <Wrench className="w-3 h-3" />
                                                        <span>Reparatur nötig</span>
                                                    </span>
                                                )}
                                            </div>

                                            {/* Title & Quantity */}
                                            <div>
                                                <h3 className="text-lg font-bold text-surface-on">{item.name}</h3>
                                                <p className="text-xs text-primary font-black mt-0.5">
                                                    {item.quantity} {item.unit || 'Stück'}
                                                </p>
                                            </div>

                                            {/* Storage Details */}
                                            <div className="p-3 rounded-2xl bg-surface-containerLowest border border-outlineVariant/20 text-xs space-y-1">
                                                <div className="flex items-center gap-1 text-surface-on font-semibold">
                                                    <MapPin className="w-3.5 h-3.5 text-surface-onVariant shrink-0" />
                                                    <span>Lagerort:</span>
                                                    <span>{STORAGE_TYPES.find(s => s.value === item.storageType)?.label || item.storageType}</span>
                                                </div>
                                                {item.locationDetails && (
                                                    <div className="text-surface-onVariant">
                                                        Detail: {item.locationDetails}
                                                    </div>
                                                )}
                                                {isHomeStorage && item.storedWithUserData && (
                                                    <div className="text-amber-700 dark:text-amber-300 font-medium">
                                                        Zuhause bei: {item.storedWithUserData.name} {item.storedWithUserData.phone ? `(${item.storedWithUserData.phone})` : ''}
                                                    </div>
                                                )}
                                            </div>

                                            {isExternal && item.ownerDetails && (
                                                <p className="text-xs text-surface-onVariant">
                                                    <span className="font-semibold">Eigentümer:</span> {item.ownerDetails}
                                                </p>
                                            )}

                                            {item.notes && (
                                                <p className="text-xs text-surface-onVariant line-clamp-2">
                                                    {item.notes}
                                                </p>
                                            )}
                                        </div>

                                        {/* Card Actions */}
                                        <div className="flex items-center justify-between pt-4 border-t border-outlineVariant/20 mt-4">
                                            <button
                                                onClick={() => handleOpenLendingModal(item._id)}
                                                className="px-3 py-1.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary text-xs font-bold transition-colors"
                                            >
                                                Verleihen
                                            </button>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleOpenEditItemModal(item)}
                                                    className="p-1.5 rounded-xl hover:bg-surface-containerHighest text-surface-onVariant hover:text-surface-on transition-colors"
                                                    title="Bearbeiten"
                                                >
                                                    <EditIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteItem(item._id)}
                                                    className="p-1.5 rounded-xl hover:bg-rose-500/10 text-surface-onVariant hover:text-rose-600 transition-colors"
                                                    title="Löschen"
                                                >
                                                    <DeleteIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* TAB 2: LENDINGS & HISTORY */}
            {activeTab === 'lendings' && (
                <div className="rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm overflow-hidden">
                    <div className="p-5 border-b border-outlineVariant/20 flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-surface-on">Verleih-Übersicht & Buchungen</h2>
                            <p className="text-xs text-surface-onVariant">Verliehenes Material an Mitglieder oder externe Gruppen</p>
                        </div>
                        <button
                            onClick={() => handleOpenLendingModal()}
                            className="px-4 py-2 rounded-2xl bg-primary text-primary-on text-xs font-bold flex items-center gap-1.5 shadow-sm"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Neue Ausleihe
                        </button>
                    </div>

                    {lendings.length === 0 ? (
                        <div className="p-12 text-center text-surface-onVariant">
                            <p className="text-base font-semibold text-surface-on">Keine Ausleihen erfasst</p>
                            <p className="text-sm mt-1">Aktuell ist kein Material als verliehen vermerkt.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm border-collapse">
                                <thead>
                                    <tr className="border-b border-outlineVariant/20 bg-surface-containerHigh/50 text-surface-onVariant text-xs uppercase font-bold tracking-wider">
                                        <th className="py-3.5 px-4">Gegenstand</th>
                                        <th className="py-3.5 px-4">Entleiher</th>
                                        <th className="py-3.5 px-4">Ausleihdatum</th>
                                        <th className="py-3.5 px-4">Rückgabe bis</th>
                                        <th className="py-3.5 px-4">Status</th>
                                        <th className="py-3.5 px-4 text-right">Aktion</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-outlineVariant/10">
                                    {lendings.map(lending => {
                                        const isActive = lending.status === 'active';
                                        const borrowerName = lending.borrowerUserData?.name || lending.borrowerName || 'Unbekannt';

                                        return (
                                            <tr key={lending._id} className="hover:bg-surface-containerHigh/30 transition-colors">
                                                <td className="py-3.5 px-4 font-bold text-surface-on">
                                                    {lending.item?.name || 'Gegenstand'}
                                                    <span className="text-xs font-normal text-surface-onVariant ml-1">
                                                        ({lending.quantity} Stück)
                                                    </span>
                                                </td>
                                                <td className="py-3.5 px-4">
                                                    <div className="font-semibold text-surface-on">{borrowerName}</div>
                                                    {lending.borrowerContact && (
                                                        <div className="text-xs text-surface-onVariant">{lending.borrowerContact}</div>
                                                    )}
                                                </td>
                                                <td className="py-3.5 px-4 font-mono text-xs text-surface-onVariant">
                                                    {lending.lendDate || '-'}
                                                </td>
                                                <td className="py-3.5 px-4 font-mono text-xs text-surface-onVariant">
                                                    {lending.expectedReturnDate || 'offen'}
                                                </td>
                                                <td className="py-3.5 px-4">
                                                    {isActive ? (
                                                        <span className="px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 text-xs font-bold">
                                                            Aktiv verliehen
                                                        </span>
                                                    ) : (
                                                        <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
                                                            Zurückgegeben am {lending.actualReturnDate}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-3.5 px-4 text-right">
                                                    {isActive && (
                                                        <button
                                                            onClick={() => handleReturnLending(lending._id)}
                                                            className="px-3 py-1.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-bold transition-all shadow-sm"
                                                        >
                                                            Rückgabe buchen
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* TAB 3: REPAIR WORKSHOP */}
            {activeTab === 'repairs' && (
                <div className="space-y-4">
                    <div className="p-5 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-surface-on">Werkstatt & Reparaturbedarf</h2>
                            <p className="text-xs text-surface-onVariant">
                                Übersicht über defekte oder wartungsbedürftige Gegenstände
                            </p>
                        </div>
                    </div>

                    {repairItems.length === 0 ? (
                        <div className="p-12 text-center text-surface-onVariant bg-surface-container rounded-3xl border border-outlineVariant/30">
                            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                            <p className="text-base font-semibold text-surface-on">Alles intakt!</p>
                            <p className="text-sm mt-1">Kein Gegenstand hat derzeit einen gemeldeten Schaden oder Reparaturbedarf.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {repairItems.map(item => (
                                <div key={item._id} className="p-5 rounded-3xl bg-surface-container border border-rose-500/30 shadow-sm space-y-3">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className="text-base font-bold text-surface-on">{item.name}</h3>
                                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-rose-500/15 text-rose-700 dark:text-rose-300">
                                                {CONDITIONS.find(c => c.value === item.condition)?.label || item.condition}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleOpenEditItemModal(item)}
                                            className="px-3 py-1.5 rounded-xl bg-primary text-primary-on text-xs font-bold"
                                        >
                                            Bearbeiten
                                        </button>
                                    </div>

                                    {item.repairNotes && (
                                        <div className="p-3 rounded-2xl bg-surface-containerLowest border border-outlineVariant/20 text-xs">
                                            <span className="font-bold text-rose-600 dark:text-rose-400 block mb-0.5">Schadensbeschreibung / Reparaturhinweis:</span>
                                            <p className="text-surface-on">{item.repairNotes}</p>
                                        </div>
                                    )}

                                    <div className="text-xs text-surface-onVariant">
                                        Lagerort: <span className="font-semibold text-surface-on">{item.locationDetails || STORAGE_TYPES.find(s => s.value === item.storageType)?.label}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* TAB 4: DECENTRALIZED HOME STORAGE */}
            {activeTab === 'home_storage' && (
                <div className="space-y-4">
                    <div className="p-5 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm">
                        <h2 className="text-lg font-bold text-surface-on">Privat & dezentral gelagertes Material</h2>
                        <p className="text-xs text-surface-onVariant">
                            Gegenstände, die dauerhaft oder vorübergehend bei Leitern oder Mitgliedern zuhause gelagert werden
                        </p>
                    </div>

                    {homeStorageItems.length === 0 ? (
                        <div className="p-12 text-center text-surface-onVariant bg-surface-container rounded-3xl border border-outlineVariant/30">
                            <p className="text-base font-semibold text-surface-on">Kein Material zuhause gelagert</p>
                            <p className="text-sm mt-1">Alle erfassten Gegenstände lagern im Pfadfinderheim oder Vereinsschuppen.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {homeStorageItems.map(item => (
                                <div key={item._id} className="p-5 rounded-3xl bg-surface-container border border-amber-500/30 shadow-sm space-y-3">
                                    <div>
                                        <h3 className="text-base font-bold text-surface-on">{item.name}</h3>
                                        <p className="text-xs text-primary font-bold">{item.quantity} {item.unit || 'Stück'}</p>
                                    </div>

                                    <div className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-xs space-y-1 text-amber-900 dark:text-amber-200">
                                        <div className="font-bold flex items-center gap-1.5">
                                            <Home className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                                            <span>Gelagert bei:</span>
                                        </div>
                                        <div>{item.storedWithUserData?.name || item.locationDetails || 'Mitglied'}</div>
                                        {item.storedWithUserData?.phone && (
                                            <div>Tel: {item.storedWithUserData.phone}</div>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => handleOpenEditItemModal(item)}
                                        className="w-full py-2 rounded-xl bg-surface-containerHighest text-surface-on hover:bg-primary hover:text-primary-on text-xs font-bold transition-all"
                                    >
                                        Lagerort / Details anpassen
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ITEM MODAL (Create / Edit) */}
            {isItemModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
                    <div className="w-full max-w-2xl rounded-3xl bg-surface-containerHigh border border-outlineVariant/40 shadow-2xl p-6 md:p-8 space-y-6 my-8">
                        <div className="flex items-center justify-between border-b border-outlineVariant/20 pb-4">
                            <h3 className="text-xl font-bold text-surface-on">
                                {editingItem ? 'Gegenstand bearbeiten' : 'Neuen Gegenstand erfassen'}
                            </h3>
                            <button
                                onClick={() => setIsItemModalOpen(false)}
                                className="p-2 rounded-full hover:bg-surface-containerHighest text-surface-onVariant transition-colors"
                            >
                                <CloseIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleItemSubmit} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
                            {/* Name & Kategorie */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Name des Gegenstands *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="z.B. Kohte 4m Komplettset, Hordentopf 15L..."
                                        value={itemFormData.name}
                                        onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Kategorie
                                    </label>
                                    <select
                                        value={itemFormData.category}
                                        onChange={(e) => setItemFormData({ ...itemFormData, category: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary font-medium"
                                    >
                                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Menge & Einheit */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Menge *
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        required
                                        value={itemFormData.quantity}
                                        onChange={(e) => setItemFormData({ ...itemFormData, quantity: parseInt(e.target.value, 10) || 1 })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm font-bold focus:outline-none focus:border-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Einheit
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Stück, Kiste, Set, Meter..."
                                        value={itemFormData.unit}
                                        onChange={(e) => setItemFormData({ ...itemFormData, unit: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary"
                                    />
                                </div>
                            </div>

                            {/* Lagerort */}
                            <div className="p-4 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30 space-y-3">
                                <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant">
                                    Lagerung & Aufbewahrungsort
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[11px] text-surface-onVariant mb-1">Typ</label>
                                        <select
                                            value={itemFormData.storageType}
                                            onChange={(e) => setItemFormData({ ...itemFormData, storageType: e.target.value })}
                                            className="w-full px-3.5 py-2 rounded-xl bg-surface-container border border-outlineVariant/40 text-surface-on text-xs font-medium"
                                        >
                                            {STORAGE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] text-surface-onVariant mb-1">
                                            {itemFormData.storageType === 'home' ? 'Mitglied auswählen' : 'Lagerplatz Details (Kiste, Regal)'}
                                        </label>
                                        {itemFormData.storageType === 'home' ? (
                                            <select
                                                value={itemFormData.storedWithUser}
                                                onChange={(e) => setItemFormData({ ...itemFormData, storedWithUser: e.target.value })}
                                                className="w-full px-3.5 py-2 rounded-xl bg-surface-container border border-outlineVariant/40 text-surface-on text-xs font-medium"
                                            >
                                                <option value="">-- Mitglied auswählen --</option>
                                                {usersList.map(u => (
                                                    <option key={u._id} value={u._id}>
                                                        {u.userData?.scoutName || u.userData?.displayName || `${u.userData?.firstName || ''} ${u.userData?.lastName || ''}` || u.email}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                placeholder="z.B. Regal 3, Kiste B..."
                                                value={itemFormData.locationDetails}
                                                onChange={(e) => setItemFormData({ ...itemFormData, locationDetails: e.target.value })}
                                                className="w-full px-3.5 py-2 rounded-xl bg-surface-container border border-outlineVariant/40 text-surface-on text-xs"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Eigentum (Stamm vs Fremdmaterial) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Eigentum
                                    </label>
                                    <select
                                        value={itemFormData.ownership}
                                        onChange={(e) => setItemFormData({ ...itemFormData, ownership: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm font-medium"
                                    >
                                        <option value="stamm">Stammeigenes Material</option>
                                        <option value="external">Fremdmaterial / Leihgabe Dritter</option>
                                    </select>
                                </div>
                                {itemFormData.ownership === 'external' && (
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                            Eigentümer Details
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="z.B. Pfadfinder Bezirk, Fam. Müller..."
                                            value={itemFormData.ownerDetails}
                                            onChange={(e) => setItemFormData({ ...itemFormData, ownerDetails: e.target.value })}
                                            className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Zustand & Reparatur */}
                            <div className="p-4 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30 space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold uppercase tracking-wider text-surface-onVariant">
                                        Zustand & Reparatur
                                    </label>
                                    <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-bold text-rose-600 dark:text-rose-400">
                                        <input
                                            type="checkbox"
                                            checked={itemFormData.needsRepair}
                                            onChange={(e) => setItemFormData({ ...itemFormData, needsRepair: e.target.checked })}
                                            className="rounded text-primary focus:ring-0"
                                        />
                                        Reparatur erforderlich
                                    </label>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[11px] text-surface-onVariant mb-1">Zustand</label>
                                        <select
                                            value={itemFormData.condition}
                                            onChange={(e) => setItemFormData({ ...itemFormData, condition: e.target.value })}
                                            className="w-full px-3.5 py-2 rounded-xl bg-surface-container border border-outlineVariant/40 text-surface-on text-xs font-medium"
                                        >
                                            {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] text-surface-onVariant mb-1">Schaden / Reparaturnotiz</label>
                                        <input
                                            type="text"
                                            placeholder="z.B. Öse ausgerissen, Reißverschluss klemmt..."
                                            value={itemFormData.repairNotes}
                                            onChange={(e) => setItemFormData({ ...itemFormData, repairNotes: e.target.value })}
                                            className="w-full px-3.5 py-2 rounded-xl bg-surface-container border border-outlineVariant/40 text-surface-on text-xs"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Bild Upload */}
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                    Foto / Bild des Gegenstands
                                </label>
                                <div className="flex items-center gap-3">
                                    <label className="cursor-pointer px-4 py-2.5 rounded-2xl border border-outlineVariant/40 bg-surface-containerLowest hover:bg-surface-container text-surface-on text-xs font-semibold flex items-center gap-2 transition-all">
                                        <UploadIcon className="w-4 h-4" />
                                        {isUploading ? 'Lädt hoch...' : 'Foto auswählen'}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            disabled={isUploading}
                                            className="hidden"
                                        />
                                    </label>
                                    {itemFormData.imageUrl && (
                                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold">
                                            ✓ Foto hinterlegt
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Notizen */}
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                    Allgemeine Notizen
                                </label>
                                <textarea
                                    rows="2"
                                    placeholder="Weitere Infos zum Gegenstand..."
                                    value={itemFormData.notes}
                                    onChange={(e) => setItemFormData({ ...itemFormData, notes: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm resize-none focus:outline-none focus:border-primary"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-outlineVariant/20">
                                <button
                                    type="button"
                                    onClick={() => setIsItemModalOpen(false)}
                                    className="px-5 py-2.5 rounded-2xl text-surface-onVariant hover:text-surface-on text-sm font-semibold transition-colors"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || isUploading}
                                    className="px-6 py-2.5 rounded-2xl bg-primary text-primary-on hover:bg-primary/90 text-sm font-bold flex items-center gap-2 transition-all shadow-md disabled:opacity-50"
                                >
                                    <SaveIcon className="w-4 h-4" />
                                    {isSubmitting ? 'Speichert...' : editingItem ? 'Speichern' : 'Gegenstand anlegen'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* LENDING MODAL */}
            {isLendingModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
                    <div className="w-full max-w-lg rounded-3xl bg-surface-containerHigh border border-outlineVariant/40 shadow-2xl p-6 md:p-8 space-y-6 my-8">
                        <div className="flex items-center justify-between border-b border-outlineVariant/20 pb-4">
                            <h3 className="text-xl font-bold text-surface-on">Material verleihen</h3>
                            <button
                                onClick={() => setIsLendingModalOpen(false)}
                                className="p-2 rounded-full hover:bg-surface-containerHighest text-surface-onVariant transition-colors"
                            >
                                <CloseIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleLendingSubmit} className="space-y-4">
                            {/* Gegenstand auswählen */}
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                    Gegenstand *
                                </label>
                                <select
                                    required
                                    value={lendingFormData.itemId}
                                    onChange={(e) => setLendingFormData({ ...lendingFormData, itemId: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm font-medium focus:outline-none focus:border-primary"
                                >
                                    {items.map(i => (
                                        <option key={i._id} value={i._id}>
                                            {i.name} ({i.quantity} {i.unit || 'Stück'} verfügbar)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Menge */}
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                    Verliehene Menge
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    required
                                    value={lendingFormData.quantity}
                                    onChange={(e) => setLendingFormData({ ...lendingFormData, quantity: parseInt(e.target.value, 10) || 1 })}
                                    className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm font-bold focus:outline-none focus:border-primary"
                                />
                            </div>

                            {/* Entleiher Typ */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Entleiher Typ
                                    </label>
                                    <select
                                        value={lendingFormData.borrowerType}
                                        onChange={(e) => setLendingFormData({ ...lendingFormData, borrowerType: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm font-medium"
                                    >
                                        <option value="member">Stammesmitglied</option>
                                        <option value="external">Extern / Andere Gruppe</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        {lendingFormData.borrowerType === 'member' ? 'Mitglied' : 'Name'}
                                    </label>
                                    {lendingFormData.borrowerType === 'member' ? (
                                        <select
                                            value={lendingFormData.borrowerUser}
                                            onChange={(e) => setLendingFormData({ ...lendingFormData, borrowerUser: e.target.value })}
                                            className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm font-medium"
                                        >
                                            <option value="">-- Mitglied wählen --</option>
                                            {usersList.map(u => (
                                                <option key={u._id} value={u._id}>
                                                    {u.userData?.scoutName || u.userData?.displayName || `${u.userData?.firstName || ''} ${u.userData?.lastName || ''}` || u.email}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            required
                                            placeholder="z.B. Pfadfinder Tiengen, Max Mustermann..."
                                            value={lendingFormData.borrowerName}
                                            onChange={(e) => setLendingFormData({ ...lendingFormData, borrowerName: e.target.value })}
                                            className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Kontakt & Zweck */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Kontakt (Tel / E-Mail)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="0170 1234567..."
                                        value={lendingFormData.borrowerContact}
                                        onChange={(e) => setLendingFormData({ ...lendingFormData, borrowerContact: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Zweck / Event
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="z.B. Pfingstlager..."
                                        value={lendingFormData.purpose}
                                        onChange={(e) => setLendingFormData({ ...lendingFormData, purpose: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm"
                                    />
                                </div>
                            </div>

                            {/* Daten */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Ausleihdatum *
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={lendingFormData.lendDate}
                                        onChange={(e) => setLendingFormData({ ...lendingFormData, lendDate: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Rückgabe erwartet am
                                    </label>
                                    <input
                                        type="date"
                                        value={lendingFormData.expectedReturnDate}
                                        onChange={(e) => setLendingFormData({ ...lendingFormData, expectedReturnDate: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-outlineVariant/20">
                                <button
                                    type="button"
                                    onClick={() => setIsLendingModalOpen(false)}
                                    className="px-5 py-2.5 rounded-2xl text-surface-onVariant hover:text-surface-on text-sm font-semibold transition-colors"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2.5 rounded-2xl bg-primary text-primary-on hover:bg-primary/90 text-sm font-bold flex items-center gap-2 transition-all shadow-md disabled:opacity-50"
                                >
                                    <SaveIcon className="w-4 h-4" />
                                    {isSubmitting ? 'Verbucht...' : 'Ausleihe verbuchen'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MaterialManagement;
